import { useEffect } from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { RefreshCw, Home, AlertTriangle } from 'lucide-react';

/**
 * 路由级错误边界（挂在 /:locale errorElement）。
 *
 * 动态模块加载失败 = 浏览器里是旧版 SPA、chunk 清单指向的文件已随新部署消失
 * （长驻标签页跨部署导航时的经典 404）。检测到时自动整页刷新一次拉取新版；
 * 30s 内只自动刷一次（防刷新循环），再次失败则落到人工兜底页。
 * 其余路由错误同样落到兜底页，替代 react-router 默认的开发者报错屏。
 */

const RELOAD_KEY = 'clawrent:chunk-reload-at';

function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return msg.includes('dynamically imported module') || msg.includes('Importing a module script failed');
}

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
      if (Date.now() - last > 30_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    } catch { /* sessionStorage 不可用（隐私模式等）则不自动刷新 */ }
  }, [error]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="glass-card max-w-md w-full p-8 text-center space-y-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
          <AlertTriangle className="h-6 w-6 text-warning" />
        </div>
        <h1 className="text-xl font-bold text-foreground">页面加载出错 / Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          平台可能刚发布了新版本，当前页面已过期。刷新即可恢复。
          <br />
          The platform may have just shipped an update — refresh to recover.
        </p>
        {error instanceof Error && (
          <p className="text-xs text-muted-foreground break-all font-mono">{error.message}</p>
        )}
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            刷新 / Refresh
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-surface-2 transition-colors"
          >
            <Home className="h-4 w-4" />
            首页 / Home
          </button>
        </div>
      </div>
    </div>
  );
}
